import { DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT } from '@ipfabric/ui-components/hooks/useDebounce';
import { FormikCheckboxField } from 'components/FormikCheckboxField/FormikCheckboxField';
import { FormikTextFieldLegacy } from 'components/FormikTextFieldLegacy/FormikTextFieldLegacy';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, useFormik } from 'formik';
import { memo, useEffect } from 'react';
import type { FC } from 'react';
import { isValidIp } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { useAutoSubmit } from 'utils/hooks/useAutoSubmit';
import { MulticastIcon } from '../icons/MulticastIcon';
import { UnicastIcon } from '../icons/UnicastIcon';
import { PATH_CHECK_LIST_PAGINATION_STEP } from './GraphPathChecksList';
import type { PathChecksHook } from './usePathChecks';

export interface PathChecksFormValues {
  dst: string;
  fail: boolean;
  multicast: boolean;
  ok: boolean;
  src: string;
  unicast: boolean;
}

const makeValidate =
  () =>
  (values: PathChecksFormValues): FormikErrors<PathChecksFormValues> => {
    const errors: FormikErrors<PathChecksFormValues> = {};
    if (isValidIp(values.src) && values.src === values.dst) {
      errors.dst = "Can't be same as Starting point";
    }

    return errors;
  };

interface Props {
  pathChecksHook: PathChecksHook;
}

const BareGraphPathChecksFilter: FC<Props> = (props) => {
  const msg = useLocale();

  const formik = useFormik({
    initialValues: {
      dst: '',
      fail: true,
      multicast: true,
      ok: true,
      src: '',
      unicast: true,
    },
    validate: makeValidate(),
    onSubmit: makeFormikOnSubmit((filters: PathChecksFormValues) => {
      props.pathChecksHook.fetchPathLookupChecks({
        filters,
        pagination: { start: 0, limit: PATH_CHECK_LIST_PAGINATION_STEP },
      });
    }),
  });

  useAutoSubmit(formik, DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT);

  // biome-ignore lint/correctness/useExhaustiveDependencies: perform once on mount
  useEffect(() => {
    formik.submitForm();
  }, []);

  return (
    <>
      <div className='graph-pathlookup-checks-filter flex items-start px-5 pt-5'>
        <div>
          <div className='flex items-center pr-2.5'>
            <label htmlFor='unicast' className='pr-lgu-3'>
              <UnicastIcon title='Unicast' width='18' height='17' />
            </label>
            <FormikCheckboxField formik={formik} name='unicast' id='unicast' />
          </div>
          <div className='flex items-center pr-2.5'>
            <label htmlFor='multicast' className='pr-lgu-3'>
              <MulticastIcon title='Multicast' width='18' height='18' />
            </label>
            <FormikCheckboxField
              formik={formik}
              name='multicast'
              id='multicast'
            />
          </div>
        </div>
        <div className='mt-lgu-2 mr-2.5 flex-1'>
          <FormikTextFieldLegacy
            className='form-control h-10'
            formik={formik}
            name='src'
            placeholder={
              msg.canvasGraph.graphSidePanel.pathChecks.filter.source
            }
          />
        </div>
        <div className='mt-lgu-2 flex flex-1 flex-col'>
          <FormikTextFieldLegacy
            className='form-control h-10'
            formik={formik}
            name='dst'
            placeholder={
              msg.canvasGraph.graphSidePanel.pathChecks.filter.destination
            }
          />
        </div>
        <div>
          <div className='flex items-center pl-2.5'>
            <label htmlFor='ok' className='pr-lgu-3'>
              <span className='graph-pathlookup-expected-status-label graph-pathlookup-expected-status-label--ok' />
            </label>
            <FormikCheckboxField formik={formik} name='ok' id='ok' />
          </div>
          <div className='flex items-center pl-2.5'>
            <label htmlFor='fail' className='pr-lgu-3'>
              <span className='graph-pathlookup-expected-status-label graph-pathlookup-expected-status-label--fail' />
            </label>
            <FormikCheckboxField formik={formik} name='fail' id='fail' />
          </div>
        </div>
      </div>
    </>
  );
};

export const GraphPathChecksFilter = memo(BareGraphPathChecksFilter);
